import * as React from 'react';
import { ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { SpinnerIcon } from '../../icons/Spinner';
import {
  TEXT_BUTTON_SIZE,
  TEXT_BUTTON_TYPE,
  buttonVariants,
  iconStyle,
  iconSizeStyle,
  iconOnlySizeStyle,
  iconOnlyChildSizeStyle
} from './styles';

export { TEXT_BUTTON_SIZE, TEXT_BUTTON_TYPE };

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  nativeType?: 'button' | 'submit' | 'reset';
  dataTestId?: string;
  ariaLabel?: string;
  loading?: boolean;
  iconOnly?: boolean;
  /** Tracking callback fired on click. */
  onTrack?: () => void;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      iconLeft,
      iconRight,
      children,
      dataTestId,
      nativeType = 'button',
      ariaLabel,
      loading = false,
      iconOnly = false,
      onTrack,
      onClick,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const iconCn = cn(iconStyle, iconSizeStyle[size || 'L']);
    const isTextVariant =
      variant === TEXT_BUTTON_TYPE.TEXT ||
      variant === TEXT_BUTTON_TYPE.TEXT_GRAY ||
      variant === TEXT_BUTTON_TYPE.DESTRUCTIVE_TEXT;
    
    const gapClass = (size === 'XL' || size === 'L') ? 'gap-2xs' : size === 'XS' ? 'gap-4xs' : 'gap-3xs';

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onTrack?.();
        onClick?.(e);
      },
      [onTrack, onClick]
    );

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          iconOnly && !isTextVariant && iconOnlySizeStyle[size || 'XS'],
          iconOnly && iconOnlyChildSizeStyle[size || 'XS'],
          className
        )}
        ref={ref}
        data-testid={dataTestId || 'text-button'}
        aria-label={ariaLabel}
        type={nativeType}
        {...props}
        onClick={handleClick}
        disabled={props.disabled || loading}>
        <span className={cn(
          'inline-flex items-center justify-center',
          gapClass,
          loading && 'invisible',
        )}>
          {iconOnly ? (
            <div className={iconCn}>{children}</div>
          ) : (
            <>
              {iconLeft && <div className={iconCn}>{iconLeft}</div>}
              {children && <span className={cn(!isTextVariant && 'px-5xs')}>{children}</span>}
              {iconRight && <div className={iconCn}>{iconRight}</div>}
            </>
          )}
        </span>
        {loading && (
          <SpinnerIcon className={`animate-spin ${iconCn} absolute`} />
        )}
      </Comp>
    );
  }
);
